import React, { useState, useEffect } from 'react';
import { api } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, AlertOctagon, Clock, AlertTriangle, TrendingUp, FileSpreadsheet, FileText, Loader2, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const REASON_LABELS = { guasto: 'Guasto', ispezione: 'Ispezione', blackout: 'Blackout' };

const ElevatorStatsPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingXlsx, setExportingXlsx] = useState(false);
  const [hotels, setHotels] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState('all');

  // Carica lista hotel (per selettore)
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/hotels');
        setHotels(res.data || []);
      } catch (_) {}
    })();
  }, []);

  // Carica stats al cambio di hotel
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const url = selectedHotel && selectedHotel !== 'all'
          ? `/elevator-stats?hotel_id=${selectedHotel}`
          : '/elevator-stats';
        const res = await api.get(url);
        setStats(res.data);
      } catch {
        toast.error('Errore nel caricamento statistiche');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedHotel]);

  const downloadExport = async (format) => {
    const setter = format === 'pdf' ? setExportingPdf : setExportingXlsx;
    try {
      setter(true);
      const params = new URLSearchParams();
      if (selectedHotel && selectedHotel !== 'all') params.set('hotel_id', selectedHotel);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const res = await api.get(`/elevator-stats/export/${format}${qs}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const stamp = new Date().toISOString().slice(0, 10);
      const hotelSlug = selectedHotel && selectedHotel !== 'all'
        ? '-' + (hotels.find(h => h.id === selectedHotel)?.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
        : '';
      a.download = `hostfix-ascensori${hotelSlug}-${stamp}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} scaricato`);
    } catch (err) {
      toast.error(`Errore export ${format.toUpperCase()}`);
    } finally {
      setter(false);
    }
  };

  if (loading && !stats) return <div className="p-6">Caricamento statistiche...</div>;
  if (!stats) return null;

  const { summary, by_elevator, top_problematic, monthly_trend, by_reason } = stats;

  const maxTrendCount = Math.max(...monthly_trend.map(t => t.count), 1);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="elevator-stats-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link to="/ascensori" className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-2">
            <ArrowLeft className="h-3 w-3" /> Torna alla gestione ascensori
          </Link>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            📊 Statistiche Ascensori
          </h1>
          <p className="text-sm text-slate-500 mt-1">Downtime, MTBF e trend dei blocchi impianti</p>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          {hotels.length > 1 && (
            <div className="min-w-[220px]">
              <label className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                <Building2 className="h-3 w-3" /> Filtra per hotel
              </label>
              <Select value={selectedHotel} onValueChange={setSelectedHotel}>
                <SelectTrigger data-testid="stats-hotel-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli hotel</SelectItem>
                  {hotels.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button
            variant="outline"
            onClick={() => downloadExport('excel')}
            disabled={exportingXlsx}
            data-testid="export-xlsx-button"
          >
            {exportingXlsx ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
            Esporta Excel
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadExport('pdf')}
            disabled={exportingPdf}
            className="bg-red-50 hover:bg-red-100 border-red-200 text-red-700"
            data-testid="export-pdf-button"
          >
            {exportingPdf ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
            Esporta PDF
          </Button>
        </div>
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-slate-500 text-sm"><AlertOctagon className="h-4 w-4" /> In blocco ora</div>
          <div className="text-3xl font-bold text-red-600 mt-1">{summary.currently_blocked}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-slate-500 text-sm"><Clock className="h-4 w-4" /> Downtime totale</div>
          <div className="text-3xl font-bold text-amber-600 mt-1">{summary.total_downtime_hours.toFixed(1)}h</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-slate-500 text-sm"><AlertTriangle className="h-4 w-4" /> Blocchi totali</div>
          <div className="text-3xl font-bold text-slate-800 mt-1">{summary.total_blockages}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-slate-500 text-sm"><TrendingUp className="h-4 w-4" /> Impianti attivi</div>
          <div className="text-3xl font-bold text-blue-700 mt-1">{summary.total_elevators}</div>
        </Card>
      </div>

      {/* Top problematic + by reason */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card className="p-4">
          <h2 className="text-lg font-semibold mb-3">Top 5 impianti più problematici</h2>
          {top_problematic.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">Nessun dato disponibile</p>
          ) : (
            <div className="space-y-3">
              {top_problematic.map((e, idx) => (
                <div key={e.elevator_id} className="flex items-center gap-3" data-testid={`top-problematic-${idx}`}>
                  <div className="text-2xl font-bold text-slate-400 w-8">{idx + 1}</div>
                  <div className="flex-1">
                    <div className="font-medium">{e.hotel_name} — {e.elevator_number}</div>
                    <div className="text-xs text-slate-500">
                      {e.blockages_count} blocchi · {e.total_downtime_hours.toFixed(1)}h totali · media {e.avg_duration_hours.toFixed(1)}h
                    </div>
                  </div>
                  <Badge className="bg-red-100 text-red-700 border-red-300">{e.blockages_count}×</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-4">
          <h2 className="text-lg font-semibold mb-3">Distribuzione per causa</h2>
          {by_reason.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">Nessun blocco registrato</p>
          ) : (
            <div className="space-y-2">
              {by_reason.map(r => {
                const total = by_reason.reduce((s, x) => s + x.count, 0);
                const pct = total > 0 ? (r.count / total) * 100 : 0;
                return (
                  <div key={r.reason}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{REASON_LABELS[r.reason] || r.reason}</span>
                      <span className="text-slate-500">{r.count} blocchi · {r.downtime_hours.toFixed(1)}h</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded overflow-hidden">
                      <div className="h-full bg-blue-600" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Monthly trend */}
      <Card className="p-4 mb-6">
        <h2 className="text-lg font-semibold mb-3">Trend mensile blocchi</h2>
        {monthly_trend.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center">Nessun dato</p>
        ) : (
          <div className="flex items-end gap-2 h-40">
            {monthly_trend.map(t => (
              <div key={t.label} className="flex-1 flex flex-col items-center" data-testid={`trend-bar-${t.label}`}>
                <div className="text-xs text-slate-500 mb-1">{t.count}</div>
                <div
                  className="w-full bg-blue-500 rounded-t hover:bg-blue-600 transition-colors"
                  style={{ height: `${(t.count / maxTrendCount) * 100}%`, minHeight: '4px' }}
                  title={`${t.label}: ${t.count} blocchi, ${t.downtime_hours.toFixed(1)}h`}
                />
                <div className="text-[10px] text-slate-400 mt-1 rotate-45 origin-left whitespace-nowrap">{t.label}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Detail table */}
      <Card className="p-4">
        <h2 className="text-lg font-semibold mb-3">Dettaglio per impianto</h2>
        {by_elevator.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center">Nessun blocco risolto da analizzare</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-slate-500 border-b">
                <tr>
                  <th className="py-2 px-3">Hotel</th>
                  <th className="py-2 px-3">N°</th>
                  <th className="py-2 px-3 text-right">Blocchi</th>
                  <th className="py-2 px-3 text-right">Downtime tot.</th>
                  <th className="py-2 px-3 text-right">Media</th>
                  <th className="py-2 px-3 text-right">Max</th>
                </tr>
              </thead>
              <tbody>
                {by_elevator.map(e => (
                  <tr key={e.elevator_id} className="border-b hover:bg-slate-50">
                    <td className="py-2 px-3">{e.hotel_name}</td>
                    <td className="py-2 px-3 font-mono">{e.elevator_number}</td>
                    <td className="py-2 px-3 text-right">{e.blockages_count}</td>
                    <td className="py-2 px-3 text-right font-mono font-semibold">{e.total_downtime_hours.toFixed(1)}h</td>
                    <td className="py-2 px-3 text-right">{e.avg_duration_hours.toFixed(1)}h</td>
                    <td className="py-2 px-3 text-right">{e.max_duration_hours.toFixed(1)}h</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ElevatorStatsPage;
